import { ITEM_STATUS_CANCELLED } from '~/consts/order';

/** Travel dates of one flight, normalised across online flight items and flight custom-offer items. */
export interface ParentFlight {
  departingDate?: string;
  arrivalDate?: string;
}

/** An order item that can anchor a post-purchase flight offer, with the flights it holds. */
export interface ParentFlightSource {
  id: string;
  isCustomOffer: boolean;
  pnr?: string;
  flights: Array<ParentFlight>;
}

function getJourneyFlights(journey?: App.Journey): Array<ParentFlight> {
  const legs: Array<App.JourneyFlight | undefined> = journey?.legs?.length
    ? journey.legs
    : [journey?.departing, journey?.returning];

  return legs
    .flatMap((leg) => leg?.flights ?? [])
    .map((flight) => ({ departingDate: flight.departing_date, arrivalDate: flight.arrival_date }));
}

/** Legs are optional on a custom offer; without them the item's travel dates stand in for one flight. */
function getCustomOfferFlights(item: App.CustomOfferItem): Array<ParentFlight> {
  const segments = (item.flight_legs ?? []).flatMap((leg) => leg.segments ?? []);

  if (!segments.length) {
    return [{ departingDate: item.start_date, arrivalDate: item.end_date || item.start_date }];
  }

  return segments.map((segment) => ({ departingDate: segment.departureDate, arrivalDate: segment.arrivalDate }));
}

function isActiveOrderItem(item: { status?: string }): boolean {
  return item.status !== ITEM_STATUS_CANCELLED;
}

/** Active flight items and flight custom-offer items on the order, in that order. */
export function getParentFlightSources(order: App.Order): Array<ParentFlightSource> {
  const flightSources = (order.flight_items ?? []).filter(isActiveOrderItem).map((item) => ({
    id: item.id,
    isCustomOffer: false,
    pnr: item.pnr_id,
    flights: getJourneyFlights(item.journey),
  }));

  const customOfferSources = (order.custom_offer_items ?? []).filter(isActiveOrderItem).flatMap((orderItem) => {
    const flightItems = (orderItem.custom_offer?.items ?? []).filter((item) => item.type === 'flight');

    return flightItems.length
      ? [
          {
            id: orderItem.id,
            isCustomOffer: true,
            pnr: flightItems[0].vendor_reference_number,
            flights: flightItems.flatMap(getCustomOfferFlights),
          },
        ]
      : [];
  });

  return [...flightSources, ...customOfferSources];
}
